import Image from "next/image";
import { FC, useState } from "react";

import { MediaBannerHighlightContent, MediaBannerHighlightType } from "@/types";
import cn from "@/utils/cn";

import { CompactModal } from "../CompactModal/CompactModal";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import ButtonOrVideoModal from "../ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";

export interface Props {
  content: MediaBannerHighlightContent;
  type: MediaBannerHighlightType | string;
  wrappedText?: boolean;
}

const MediaBannerHighlightCardContent: FC<Props> = ({ content, type, wrappedText }) => {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div
      className={cn(
        "absolute top-0 left-0 h-full flex w-full flex-col justify-end md:static z-[1] md:box-material-primary md:backdrop-blur-[100px] text-center pt-xxl md:px-l md:py-xxl text-white rounded-xl md:my-s",
        type == MediaBannerHighlightType.FULL_SCREEN && wrappedText && "!justify-between box-material-primary backdrop-blur-md h-auto static",
        type == MediaBannerHighlightType.SEPARATOR && "media-highlight-container absolute md:backdrop-blur-[100px]",
        type == MediaBannerHighlightType.SEPARATOR && content.primaryCta && wrappedText && "relative box-material-primary backdrop-blur-[100px]",
        content.logo && "justify-between md:justify-end",
        type == MediaBannerHighlightType.BOXED && !content.logo && "justify-end",
      )}
    >
      {content.logo && <Image className="mx-auto" src={content.logo?.url} alt={content.logo?.title || ""} width={160} height={64} />}
      <div
        className={cn(
          "px-m md:px-0 pb-l md:pb-0 media-highlight-content relative",
          type == MediaBannerHighlightType.SEPARATOR && wrappedText && "!pt-0 !text-center",
          type == MediaBannerHighlightType.SEPARATOR && !wrappedText && "after:!rounded-none",
          type == MediaBannerHighlightType.FULL_SCREEN && wrappedText ? "items-center" : " pt-3xl md:pt-0 text-left md:text-center",
          type == MediaBannerHighlightType.FULL_SCREEN && !wrappedText && "after:!rounded-none",
        )}
      >
        <h2
          className={cn(
            "font-primary text-l font-medium uppercase",
            type == MediaBannerHighlightType.SEPARATOR && content.primaryCta && wrappedText ? "text-center" : "",
            type == MediaBannerHighlightType.SEPARATOR && "md:text-xl",
          )}
        >
          {content.title}
        </h2>
        {content.subtitle && <h4 className="uppercase mt-m text-xs">{content.subtitle}</h4>}
        {content.description && (
          <p
            className={cn(
              "mt-m",
              type == MediaBannerHighlightType.SEPARATOR && "text-left sm:text-center",
              (type == MediaBannerHighlightType.FULL_SCREEN || type == MediaBannerHighlightType.SEPARATOR) && wrappedText && "text-center",
            )}
          >
            {content.description}
          </p>
        )}
        {content.primaryCta &&
          (content.modal ? (
            <>
              <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                customClass={cn(
                  content.primaryCta && content.secondaryCta && "!w-full text-center",
                  (type == MediaBannerHighlightType.SEPARATOR || (type == MediaBannerHighlightType.FULL_SCREEN && wrappedText)) && "mx-auto",
                  "w-fit mt-xl flex btn-medium btn-primary-white justify-center md:mx-auto !border-white hover:!border-0 !text-s",
                )}
                onClick={() => setModalOpen(true)}
              >
                {content.modalTitle || content.primaryCta.label}
              </Button>
              <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <ModalContent>
                  <CompactModal
                    onClose={() => setModalOpen(false)}
                    title={content.modal.title}
                    image={content.modal.image}
                    bodyText={content.modal.text}
                    cta={content.modal.cta}
                  ></CompactModal>
                </ModalContent>
              </Modal>
            </>
          ) : (
            <ButtonOrVideoModal
              cta={content.primaryCta}
              btnClasses={cn(
                "w-fit mt-xl flex btn-medium btn-primary-white justify-center md:mx-auto !border-white hover:!border-0 !text-s",
                content.primaryCta.type !== "videoModal" && "btn-medium btn-primary-white shadow-strong !border-white",
                content.primaryCta && content.secondaryCta && "!w-full text-center",
                (type == MediaBannerHighlightType.SEPARATOR || type == MediaBannerHighlightType.FULL_SCREEN) && wrappedText && "mx-auto",
              )}
              btnSize={ButtonSize.MEDIUM}
            />
          ))}
        {content.secondaryCta && (
          <Button
            href={content?.secondaryCta?.link?.url}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.SECONDARY}
            theme={ButtonTheme.WHITE}
            customClass={cn(
              content.primaryCta && content.secondaryCta && "!w-full text-center",
              "mt-m inline-block w-fit",
              type == MediaBannerHighlightType.SEPARATOR && wrappedText && "mx-auto",
            )}
          >
            {content.secondaryCta.label}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MediaBannerHighlightCardContent;
